import { useState } from 'react';

import {
  PIPELINE_RUN_LIMIT_DEFAULT,
  PIPELINE_RUN_LIMIT_MAX,
  PIPELINE_RUN_REASON_MAX_LENGTH,
  type ContractType,
  type PipelineRunFilter,
} from '@clm/shared-types';

import { requestPipelineRun, runPipelineNow } from '../../api/pipeline-runs';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { TagInput } from '../../components/TagInput';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { APPROVE_ROLES, REQUEST_ROLES, runProblemHead, toRunProblem, type RunProblem } from './shared';

/**
 * Request a run — CLM-83 (D-83-2, D-83-3, D-83-5).
 *
 * Shown to everyone. **Request run** dims for an identity without the
 * contributor role; **Run now** is hidden for an identity that is known not to
 * be an admin (`mayAttempt` — usability only; the API is the gate, NFR-06, and
 * an identity that names no role sees both). Run now creates *and* approves in
 * one action, so the worker picks the run up within its poll interval.
 *
 * `contract_type` and `tags` are recorded on the run but not applied by the
 * worker today (the spec's §Where the codebase does not support) — the form
 * says so under the fields.
 */

export function RequestRunForm({ onDone }: { readonly onDone: () => void }) {
  const { identity } = useIdentity();
  const mayRequest = mayAttempt(identity, REQUEST_ROLES);
  const mayRunNow = mayAttempt(identity, APPROVE_ROLES);

  const [contractType, setContractType] = useState<'all' | ContractType>('all');
  const [contractKey, setContractKey] = useState('');
  const [tags, setTags] = useState<readonly string[]>([]);
  const [limit, setLimit] = useState(String(PIPELINE_RUN_LIMIT_DEFAULT));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<RunProblem | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const filter = (): PipelineRunFilter => {
    const parsedLimit = Number.parseInt(limit, 10);
    return {
      limit: Number.isFinite(parsedLimit) ? parsedLimit : PIPELINE_RUN_LIMIT_DEFAULT,
      ...(contractType === 'all' ? {} : { contract_type: contractType }),
      ...(contractKey.trim() === '' ? {} : { contract_key: contractKey.trim() }),
      ...(tags.length === 0 ? {} : { tags: [...tags] }),
    };
  };

  const submit = async (mode: 'request' | 'now') => {
    setBusy(true);
    setProblem(null);
    setDone(null);
    try {
      const body = { filter: filter(), reason: reason.trim() };
      const response = mode === 'now' ? await runPipelineNow(body) : await requestPipelineRun(body);
      setDone(mode === 'now' ? `Run ${response.run.pipeline_run_id} approved — the worker starts it shortly` : `Run ${response.run.pipeline_run_id} requested — awaiting an admin`);
      setReason('');
      onDone();
    } catch (error) {
      setProblem(toRunProblem(error));
    } finally {
      setBusy(false);
    }
  };

  const reasonMissing = reason.trim() === '';

  return (
    <div className="vform" role="group" aria-label="Request a pipeline run">
      <div className="field">
        <label htmlFor="run-contract-type">Run contract type</label>
        <select
          id="run-contract-type"
          value={contractType}
          disabled={busy}
          onChange={(event) => setContractType(event.target.value as 'all' | ContractType)}
        >
          <option value="all">All types</option>
          <option value="MSA">MSA</option>
          <option value="NDA">NDA</option>
          <option value="SOW">SOW</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="run-contract-key">Contract key (optional)</label>
        <input
          id="run-contract-key"
          type="text"
          value={contractKey}
          disabled={busy}
          placeholder="e.g. AGM-0001 or AGM-SOW-0001"
          onChange={(event) => setContractKey(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="run-tags">Run tags (all of)</label>
        <TagInput id="run-tags" mode="filter" value={tags} onChange={setTags} disabled={busy} max={10} />
      </div>
      <div className="field">
        <label htmlFor="run-limit">Limit (1–{PIPELINE_RUN_LIMIT_MAX})</label>
        <input
          id="run-limit"
          type="number"
          min={1}
          max={PIPELINE_RUN_LIMIT_MAX}
          value={limit}
          disabled={busy}
          onChange={(event) => setLimit(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="run-reason">Reason (required)</label>
        <textarea
          id="run-reason"
          value={reason}
          disabled={busy}
          maxLength={PIPELINE_RUN_REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <p className="mut">
        Type and tags are recorded on the run; the worker applies the key and the limit today.
      </p>
      <div className="vacts">
        <button
          type="button"
          className="btn pri"
          disabled={!mayRequest || busy || reasonMissing}
          title={mayRequest ? undefined : 'Contributor role required'}
          onClick={() => void submit('request')}
        >
          Request run
        </button>
        <button
          type="button"
          className="btn"
          hidden={!mayRunNow}
          disabled={busy || reasonMissing}
          title="Admin — creates and approves the run in one action"
          onClick={() => void submit('now')}
        >
          Run now
        </button>
      </div>
      {done === null ? null : (
        <p role="status" className="mut">
          {done}
        </p>
      )}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{runProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
          {problem.correlation_id === null ? null : (
            <p className="mut">
              Correlation <code>{problem.correlation_id}</code>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
